import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, NavLink } from 'react-router-dom';
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { SidebarContent } from "@/components/ui/sidebar";
import { 
  ChevronRight,
  Calendar,
  CheckSquare,
  List,
  History,
  KanbanSquare,
  MessageSquare,
  LayoutDashboard,
  Clock,
  HelpCircle
} from 'lucide-react';
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { Button } from "@/components/ui/button";
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { cn } from "@/lib/utils";
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import WorkLogDialog from '../workspace/WorkLogDialog';
import RemindersSection from './RemindersSection';
import TicketsSection from './TicketsSection';
import TaskSuggestionsSection from './TaskSuggestionsSection';
import type { Reminder } from '@/components/workspace/RemindersComponent';

interface WorkspaceSidebarProps {
  openReminderDialog?: () => void;
}

const WorkspaceSidebar: React.FC<WorkspaceSidebarProps> = ({ 
  openReminderDialog
}) => {
  const [upcomingReminders, setUpcomingReminders] = useState<Reminder[]>([]);
  const [reminderCount, setReminderCount] = useState(0);
  const [isWorkLogOpen, setIsWorkLogOpen] = useState(false);
  const [pendingTasks, setPendingTasks] = useState(0);
  const { user, isAdmin, hasHubspotSupport, userRoles } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const isMisTareasActive = isAdmin 
    ? location.pathname === '/workspace/tasks-admin'
    : location.pathname === '/workspace/tasks';
  const isCalendarActive = location.search.includes('tab=calendar');
  const isHistorialReunionesActive = location.pathname === '/workspace/salesops/history';
  const isDashboardActive = location.pathname === '/status-prioridades';
  const isUsuarioPrincipal = userRoles?.includes('Usuario Principal');
  const shouldShowHistorialReuniones = true;
  const isRevenueHubMember = user?.organization_name === 'Revenue Hub';

  const getLinks = () => {
    const baseLinks = [
      {
        to: "/dashboard",
        label: "Panel",
        icon: <Calendar className="h-4 w-4" />,
      },
      {
        to: "/workspace/tasks",
        label: "Mis Tareas",
        icon: <KanbanSquare className="h-4 w-4" />,
      },
      {
        to: "/workspace",
        label: "Workspace",
        icon: <Calendar className="h-4 w-4" />,
      },
      {
        to: "/work-history",
        label: "Historial de Atención",
        icon: <Clock className="h-4 w-4" />,
      },
      {
        to: "/support",
        label: "Soporte",
        icon: <MessageSquare className="h-4 w-4" />,
      },
    ];
    
    baseLinks.push({
      to: "/workspace/salesops/history",
      label: "Historial de Reuniones",
      icon: <History className="h-4 w-4" />,
    });
    
    if (isAdmin || hasHubspotSupport) {
      baseLinks.push({
        to: "/status-prioridades",
        label: "Dashboard Principal",
        icon: <LayoutDashboard className="h-4 w-4" />,
      });
    }
    
    return baseLinks;
  };

  useEffect(() => {
    if (user?.id) {
      fetchReminders();
      fetchPendingTasks();
    }
  }, [user]);

  const fetchReminders = async () => {
    if (!user) return;
    
    try {
      const now = new Date();
      const { data, error } = await supabase
        .from('reminders')
        .select('*')
        .eq('user_id', user.id)
        .gte('due_date', now.toISOString())
        .order('due_date', { ascending: true })
        .limit(3);

      if (error) throw error;
      
      setUpcomingReminders(data || []);
      
      const countResponse = await supabase
        .from('reminders')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .gte('due_date', now.toISOString());
        
      if (countResponse.error) {
        console.error('Error fetching reminder count:', countResponse.error);
        return;
      }
      
      setReminderCount(countResponse.count || 0);
    } catch (error) {
      console.error('Error fetching upcoming reminders:', error);
    }
  };

  const fetchPendingTasks = async () => {
    if (!user) return;
    
    try {
      const now = new Date();
      const { count, error } = await supabase
        .from('kanban_cards')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'Por hacer')
        .eq('user_id', user.id)
        .lt('due_date', now.toISOString());
      
      if (error) {
        console.error('Error fetching pending tasks:', error);
        throw error;
      }
      
      setPendingTasks(count || 0);
      
    } catch (error) {
      console.error('Error fetching pending tasks:', error);
    }
  };

  const handleNewReminderClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (openReminderDialog) {
      openReminderDialog();
    } else {
      navigate('/workspace?tab=reminders&new=true');
    }
  };

  const handleNavigateToReminders = (reminderId?: string) => {
    const url = reminderId 
      ? `/workspace?tab=reminders&id=${reminderId}` 
      : '/workspace?tab=reminders';
    navigate(url);
  };

  const handleNavigateToMisTareas = () => {
    if (isAdmin) {
      navigate('/workspace/tasks-admin');
    } else {
      navigate('/workspace/tasks');
    }
  };

  const handleNavigateToCalendar = () => {
    navigate('/workspace?tab=calendar');
  };

  const handleNavigateToHistorialReuniones = () => {
    navigate('/workspace/salesops/history');
  };

  const handleNavigateToDashboard = () => {
    navigate('/status-prioridades');
  };

  return (
    <SidebarContent>
      <TicketsSection />
      
      <div className="px-4 mb-2 mt-2">
        <button 
          className="flex items-center justify-between w-full py-3 px-4 text-sm font-medium rounded-lg transition-colors 
            bg-[#E8F0FE] text-[#4C8EF1] border-[#4C8EF1]/30 hover:bg-[#D8E6FD]"
          onClick={() => navigate('/booking-help')}
        >
          <div className="flex items-center">
            <HelpCircle className="h-4 w-4 mr-2" />
            <span className="font-medium">Agenda una reunión acá</span>
          </div>
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
      
      <div className="px-4 mb-2">
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Espacios de Trabajo</h3>
        
        <div className="space-y-1">
          <button 
            onClick={handleNavigateToDashboard}
            className={`flex items-center justify-between w-full py-3 px-4 text-sm font-medium rounded-lg transition-colors 
              bg-[#E8F0FE] text-[#4C8EF1] border-[#4C8EF1]/30 hover:bg-[#D8E6FD]`}
          >
            <div className="flex items-center">
              <LayoutDashboard className="h-4 w-4 mr-2" />
              <span className="font-medium">Dashboard Principal</span>
            </div>
            <ChevronRight className="h-4 w-4" />
          </button>
        
          <button 
            className={`flex items-center justify-between w-full py-2 px-3 rounded-md hover:bg-gray-100 ${isMisTareasActive ? 'bg-blue-50 text-blue-600' : 'text-gray-700'}`}
            onClick={handleNavigateToMisTareas}
          >
            <div className="flex items-center">
              <div className={`w-6 h-6 ${isMisTareasActive ? 'text-blue-600' : 'text-gray-500'} mr-3 flex items-center justify-center`}>
                <CheckSquare className="h-5 w-5" />
              </div>
              <span className={`text-sm ${isMisTareasActive ? 'font-medium' : ''}`}>Mis Tareas</span>
            </div>
            
            {pendingTasks > 0 && (
              <Badge variant="error" className="ml-2">
                {pendingTasks}
              </Badge>
            )}
          </button>
          
          {getLinks().length > 0 && (
            <div className="hidden">
              {getLinks().map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  className={
                    (isLinkActive) => cn(
                      "block px-4 py-2 rounded-md hover:bg-muted hover:text-foreground transition-colors",
                      isLinkActive ? "bg-muted font-medium text-blue-600" : "text-muted-foreground"
                    )
                  }
                >
                  {link.icon && <span className="mr-2">{link.icon}</span>}
                  {link.label}
                </NavLink>
              ))}
            </div>
          )}
          
          <button 
            className={`flex items-center w-full py-2 px-3 rounded-md hover:bg-gray-100 ${isCalendarActive ? 'bg-blue-50 text-blue-600' : 'text-gray-700'}`}
            onClick={handleNavigateToCalendar}
          >
            <div className={`w-6 h-6 ${isCalendarActive ? 'text-blue-600' : 'text-gray-500'} mr-3 flex items-center justify-center`}>
              <Calendar className="h-5 w-5" />
            </div>
            <span className={`text-sm ${isCalendarActive ? 'font-medium' : ''}`}>Calendario</span>
          </button>
          
          <button 
            onClick={handleNavigateToHistorialReuniones}
            className={`flex items-center w-full py-2 px-3 rounded-md hover:bg-gray-100 ${isHistorialReunionesActive ? 'bg-blue-50 text-blue-600' : 'text-gray-700'}`}
          >
            <div className={`w-6 h-6 ${isHistorialReunionesActive ? 'text-blue-600' : 'text-gray-500'} mr-3 flex items-center justify-center`}>
              <History className="h-5 w-5" />
            </div>
            <span className={`text-sm ${isHistorialReunionesActive ? 'font-medium' : ''}`}>Historial de Reuniones</span>
          </button>
        </div>
      </div>
      
      {user?.organization_name && <TaskSuggestionsSection />}
      
      <div className="flex-grow"></div>
      
      <RemindersSection 
        upcomingReminders={upcomingReminders} 
        reminderCount={reminderCount}
        onNewReminderClick={handleNewReminderClick}
        onNavigateToReminders={handleNavigateToReminders}
      />
      
      {isRevenueHubMember && (
        <div className="px-4 py-3 border-t border-gray-200">
          <Button 
            variant="outline" 
            size="sm" 
            className="w-full flex items-center justify-center"
            onClick={() => setIsWorkLogOpen(true)}
          >
            <Clock className="mr-2 h-4 w-4" />
            Registrar Hora
          </Button>
        </div>
      )}
      
      <WorkLogDialog open={isWorkLogOpen} onOpenChange={setIsWorkLogOpen} />
    </SidebarContent>
  );
};

export default WorkspaceSidebar;
